import React, { useReducer, useRef, useEffect, useState, useCallback } from "react";
import cn from "classnames";
import PropTypes from "prop-types";
import Draggable from "react-draggable";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import withStyles from "components/with-styles";
import styles from "./styles/timeline-segment.css.js";
import {
  getDragValues,
  getResizeEndValue,
  DEFAULT_DURATION,
  MIN_DURATION,
  secondsToPixels,
  pixelsToSeconds,
  isOverlapped,
  segmentReducer,
  formatSegmentLabel,
} from "./timeline-utils";
import SegmentDialog from "./timeline-segment-dialog";

const TimelineSegment = ({ element, onDelete, period, segment = {}, disabled = false, onChange, classes }) => {
  const getDefaultValues = useCallback(
    ({ showAlways, period }) => ({
      start: showAlways ? 0 : segment.start || 0,
      end: showAlways && period ? period : segment.end || (segment.start || 0) + DEFAULT_DURATION,
    }),
    [segment.end, segment.start],
  );

  const [open, setOpen] = useState(false);
  const isFirst = useRef(true);
  const dragging = useRef(false);
  const [state, dispatch] = useReducer(segmentReducer, getDefaultValues({ showAlways: element.showAlways, period }));
  const width = secondsToPixels(state.end - state.start) || secondsToPixels(MIN_DURATION);

  useEffect(
    () => {
      if (!isFirst.current) {
        dispatch({ type: "reset", ...getDefaultValues({ showAlways: element.showAlways, period }) });
      }
    },
    [element.showAlways, getDefaultValues, period, segment.id],
  );

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
    }
  }, []);

  const handleDrag = (event, { x }) => {
    dragging.current = true;
    const dragValues = getDragValues({
      value: x < 0 ? 0 : pixelsToSeconds(x),
      period,
      duration: state.end - state.start,
    });

    if (!isOverlapped({ id: segment.id, segments: element.segments, ...dragValues })) {
      dispatch({ type: "drag", value: x < 0 ? 0 : pixelsToSeconds(x), period });
    }
  };

  const handleDragStop = () => {
    if (dragging.current) {
      dragging.current = false;
      if (!isOverlapped({ id: segment.id, start: state.start, end: state.end, segments: element.segments })) {
        onChange({
          ...segment,
          ...state,
        });
      }
    } else {
      setOpen(true);
    }
  };

  const handleResize = (event, { size }) => {
    const end = getResizeEndValue({
      value: pixelsToSeconds(size.width, false),
      start: state.start,
      period,
    });

    if (!isOverlapped({ id: segment.id, segments: element.segments, start: state.start, end })) {
      dispatch({
        type: "resize",
        value: pixelsToSeconds(size.width, false),
        period,
      });
    }
  };

  const handleResizeStop = (event, { size }) => {
    const value = pixelsToSeconds(size.width);
    const end = getResizeEndValue({
      value,
      start: state.start,
      period,
    });

    if (!isOverlapped({ id: segment.id, start: segment.start, end, segments: element.segments })) {
      dispatch({
        type: "resize",
        value,
        period,
      });
      onChange({
        ...segment,
        end,
      });
    }
  };

  return (
    <>
      <Draggable
        disabled={disabled}
        onDrag={handleDrag}
        onStop={handleDragStop}
        axis="x"
        bounds="parent"
        cancel=".react-resizable-handle"
        position={{ x: secondsToPixels(state.start || 0), y: 0 }}
      >
        <Resizable
          width={width}
          height={0}
          className={cn(classes.resizable, {
            [classes.resizable__disabled]: disabled,
          })}
          resizeHandles={disabled ? [] : ["e"]}
          axis="x"
          onResize={handleResize}
          onResizeStop={handleResizeStop}
        >
          <div
            onClick={e => e.stopPropagation()}
            className={classes.content__wrapper}
            style={{
              width,
            }}
          >
            <div className={classes.content__text}>{formatSegmentLabel({ label: element.name, width })}</div>
          </div>
        </Resizable>
      </Draggable>
      {!element.showAlways && (
        <SegmentDialog
          open={open}
          setOpen={setOpen}
          element={element}
          onDelete={onDelete}
          period={period}
          segment={segment}
          disabled={disabled}
          onChange={onChange}
        />
      )}
    </>
  );
};

TimelineSegment.propTypes = {
  /*
   * Elemento a representar en el timeline
   */
  element: PropTypes.shape({
    layoutId: PropTypes.string.isRequired,
    showAlways: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        start: PropTypes.number,
        end: PropTypes.number,
      }),
    ),
  }),
  /*
   * Segmento a representar en el componente
   */
  segment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  /*
   * Indica si está deshabilitado
   */
  disabled: PropTypes.bool,
  /*
   * Callback que se llama con el segmento a borrar
   */
  onDelete: PropTypes.func,
  /*
   * Callback que se llama con el elemento actualizado
   */
  onChange: PropTypes.func,
  /*
   * Período que representa el timeline (en segundos)
   */
  period: PropTypes.number,
};

export default withStyles(styles)(TimelineSegment);
